import {
	AlertDialog,
	FormattedText,
	Icon
} from "client/Components";
import { useDataInputDialog } from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import {
	Fragment,
	ReactElement,
	ReactNode,
	useMemo
} from "react";
import styles from "./InfoPlaceHoldersDialog.module.less";

export type InfoPlaceHoldersDialogProps = {
	onClose(): void;
};

export const InfoPlaceHoldersDialog = (props: InfoPlaceHoldersDialogProps): ReactElement => {
	const {
		onClose
	} = props;

	const elements = useMemo(
		(): readonly ReactNode[] => {
			return parseMoreInfo(localize("_IntegrationBuilderMoreInfo"));
		},
		[]
	);

	return (
		<AlertDialog
			title={localize("_IntegrationBuilderMoreInfoTitle")}
			onClose={onClose}
			showCloseButton
			trackingId="integration-builder-more-info-dialog"
		>
			{elements}
		</AlertDialog>
	);
};
InfoPlaceHoldersDialog.displayName = "InfoPlaceHoldersDialog";

type ParseContext = {
	lines: readonly string[];
	lineNumber: number;
	indentionLevel: number;
};

function parseMoreInfo(text: string): readonly ReactNode[] {
	const result: ReactNode[] = [];

	const lines: readonly string[] = text
		.split("\n");

	const context: ParseContext = {
		lines,
		lineNumber: 0,
		indentionLevel: 0
	};

	while (context.lineNumber < context.lines.length) {
		result.push(parseElement(context));
	}

	return result.map((x, i) => (
		<Fragment key={i}>
			{x}
		</Fragment>
	));
}

function parseListItem(context: ParseContext): ReactNode {
	const line: string = context.lines[context.lineNumber];
	const trimmedLine: string = line.trimStart();
	const indentionLevel: number = getIndentionLevel(line);

	if (indentionLevel < context.indentionLevel) {
		return null;
	}

	if (trimmedLine.length === 0) {
		context.lineNumber++;
		return <br />;
	}

	if (trimmedLine.charAt(0) !== "-") {
		return null;
	}

	if (indentionLevel > context.indentionLevel) {
		return parseList(context);
	}

	const lineWithoutDash: string = trimmedLine.substring(1)
		.trimStart();

	context.lineNumber++;

	return (
		<li>
			{createFormattedText(lineWithoutDash)}
		</li>
	);
}

function parseList(context: ParseContext): ReactNode {
	const line: string = context.lines[context.lineNumber];
	const originalIndentionLevel: number = context.indentionLevel;
	const indentionLevel: number = getIndentionLevel(line);

	if (indentionLevel < originalIndentionLevel) {
		throw new Error("should not be parsing a list");
	}

	// Don't skip a level
	context.indentionLevel = Math.min(originalIndentionLevel + 1, indentionLevel);

	const children: ReactNode[] = [];

	while (context.lineNumber < context.lines.length) {
		const child: ReactNode = parseListItem(context);
		if (child === null) {
			break;
		}

		children.push(child);
	}

	context.indentionLevel = originalIndentionLevel;

	return (
		<>
			<ul className={styles.list}>
				{children.map((x, i) => (
					<Fragment key={i}>
						{x}
					</Fragment>
				))}
			</ul>
		</>
	);
}

function parseElement(context: ParseContext): ReactNode {
	const line: string = context.lines[context.lineNumber];
	const trimmedLine: string = line.trimStart();

	if (trimmedLine === "") {
		context.lineNumber++;
		return <br />;
	}

	switch (trimmedLine.charAt(0)) {
		case "-":
			return parseList(context);
		case "#":
			const lineWithoutPound: string = trimmedLine.substring(1)
				.trimStart();

			context.lineNumber++;

			return (
				<h4 className={styles.title}>
					{createFormattedText(lineWithoutPound)}
				</h4>
			);
		default:
			context.lineNumber++;
			return (
				<p className={styles.paragraph}>
					{createFormattedText(trimmedLine)}
				</p>
			);
	}
}

function createFormattedText(text: string): ReactElement {
	return (
		<FormattedText
			text={text}
			thingsToReplace={[
				{
					what: "{{PlaceholderIcon}}",
					filler: (
						<Icon
							value={{
								kind: "font",
								name: "brackets-curly",
								color: "primary"
							}}
						/>
					)
				},
				{
					what: "{{ExpressionIcon}}",
					filler: (
						<Icon
							value={{
								kind: "font",
								name: "function",
								color: "primary"
							}}
						/>
					)
				}
			]}
		/>
	);
}

function getIndentionLevel(line: string): number {
	for (let i = 0; i < line.length; i++) {
		if (line.charAt(i) !== "\t") {
			return i;
		}
	}

	return line.length;
}

export type InfoPlaceHoldersDialogInitialData = Omit<
	InfoPlaceHoldersDialogProps,
	"onClose"
>;

export function useInfoPlaceHoldersDialog(): [
	element: ReactElement | null,
	open: (props: InfoPlaceHoldersDialogInitialData) => Promise<TryResult<void>>
] {
	return useDataInputDialog(
		(props, onClose) => (
			<InfoPlaceHoldersDialog
				{...props}
				onClose={onClose}
			/>
		)
	);
}
